/**
 * House tickets — fetch + shape for BetDetail ticket lane drawer.
 * Public read: GET /api/betting/bet-house/:betId/tickets (same as mobile betting slice).
 */

import { fmtFull, formatLastUpdated, getFixtureScore } from "./betDetailHelpers";
import { STATUS_DONE, STATUS_LIVE } from "../components/bondhouse/FixtureStatus";

const num = (v) => Number(v) || 0;

export const getLegFixtureStatusLabel = (fixture) => {
  const short = (fixture?.status?.short || "NS").toUpperCase();
  const elapsed = fixture?.status?.elapsed;

  if (STATUS_LIVE.has(short)) {
    if (elapsed != null) return `LIVE · ${elapsed}'`;
    return short || "LIVE";
  }

  if (STATUS_DONE.has(short)) {
    const home =
      fixture?.score?.fulltime?.home ??
      fixture?.goals?.home ??
      null;
    const away =
      fixture?.score?.fulltime?.away ??
      fixture?.goals?.away ??
      null;
    if (home != null || away != null) {
      return `${short || "FT"} · ${home ?? 0}–${away ?? 0}`;
    }
    return short || "FT";
  }

  return short || "NS";
};

export const TICKET_STATUS_FILTERS = ["all", "pending", "won", "paid", "lost", "voided", "refunded"];

export const fetchHouseTickets = async (betId, apiBaseUrl) => {
  const res = await fetch(`${apiBaseUrl}/api/betting/bet-house/${betId}/tickets`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  const body = await res.json();
  if (!res.ok) {
    throw new Error(body.message || body.error || "Failed to load tickets");
  }
  const rows = Array.isArray(body.data) ? body.data : [];
  return rows.map(normalizeHouseTicket);
};

export const normalizeHouseTicket = (doc) => {
  const legs = (Array.isArray(doc?.fixtures) ? doc.fixtures : []).map((f) => {
    const fx = f.fixture || f;
    const odd = f.selectedOdd || {};
    const { short } = getFixtureScore(fx);
    const statusShort = (short || "NS").toUpperCase();
    const homeTeam = fx?.teams?.home || null;
    const awayTeam = fx?.teams?.away || null;
    return {
      home: homeTeam?.name || "Home",
      away: awayTeam?.name || "Away",
      homeTeam,
      awayTeam,
      pick: odd.section && odd.key ? `${odd.section} · ${odd.key}` : odd.key || "—",
      odds: num(odd.value),
      status: f.status || doc.status || "pending",
      fixtureStatus: statusShort,
      fixtureStatusLabel: getLegFixtureStatusLabel(fx),
      isLive: STATUS_LIVE.has(statusShort),
      isDone: STATUS_DONE.has(statusShort),
    };
  });

  const placedAt = doc.createdAt || doc.updatedAt;

  return {
    id: doc._id,
    shortId: String(doc._id || "").slice(-6).toUpperCase(),
    bettor: doc.user?.name || "Bettor",
    amount: num(doc.amountBet),
    potential: num(doc.potentialWinnings),
    odds: num(doc.totalOddsValue),
    status: doc.status || "pending",
    picks: legs.length,
    legs,
    placedAt,
    when: placedAt ? formatLastUpdated(placedAt) : null,
  };
};

export const getTicketPulse = (tickets) => {
  if (!tickets.length) {
    return { total: 0, open: 0, volume: 0, exposure: 0 };
  }
  const openTickets = tickets.filter((t) => t.status === "pending");
  const volume = tickets.reduce((s, t) => s + t.amount, 0);
  const exposure = openTickets.reduce((s, t) => s + Math.max(0, t.potential - t.amount), 0);
  return {
    total: tickets.length,
    open: openTickets.length,
    volume,
    exposure,
  };
};

export const filterHouseTickets = (tickets, statusFilter) => {
  if (!statusFilter || statusFilter === "all") return tickets;
  return tickets.filter((t) => t.status === statusFilter);
};

export const fmtTicketCash = (n) => `${fmtFull(n)} Cash`;

export const getTicketStatusCounts = (tickets) => {
  const counts = { all: tickets.length };
  for (const t of tickets) {
    const key = t.status || "pending";
    counts[key] = (counts[key] || 0) + 1;
  }
  return counts;
};

export const ticketStatusTone = (status) => {
  switch (status) {
    case "won":
    case "paid":
      return "success";
    case "lost":
      return "error";
    case "voided":
    case "refunded":
      return "muted";
    default:
      return "pending";
  }
};
